import React from 'react';
import { Users, MapPin, Music2, Clock, Star, Heart } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Services = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();
  const scrollRef3 = useScrollAnimation();

  return (
    <section id="personne" className="section-modern relative">
      {/* Fond avec texture */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/50 to-black"></div>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* En-tête de section */}
          <div
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-12 sm:mb-20"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light heading-modern mb-4 sm:mb-6 px-4">
              Notre <span className="heading-accent">Expertise</span>
            </h2>
            <div className="w-24 sm:w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-6 sm:mb-8"></div>
            <p className="text-base sm:text-lg md:text-xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed px-4">
              Un homme et une passion au service de l'excellence musicale alsacienne.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Image avec effet */}
            <div
              ref={scrollRef2 as React.RefObject<HTMLDivElement>}
              className="scroll-reveal relative px-4 sm:px-0"
            >
              <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl">
                <img
                  src="https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=1200"
                  alt="Piano de qualité professionnelle"
                  className="w-full h-64 sm:h-80 md:h-96 object-cover transition-transform duration-700 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                </div>
              </div>

              {/* Effet lumineux */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-400/20 rounded-full blur-2xl animate-glow-pulse"></div>
            </div>

            {/* Contenu */}
            <div
              ref={scrollRef3 as React.RefObject<HTMLDivElement>}
              className="scroll-reveal space-y-6 sm:space-y-8 px-4 sm:px-0"
            >
              <div className="glass-card interactive-card rounded-xl sm:rounded-2xl p-6 sm:p-8 light-effect">
                <div className="flex items-start space-x-4 sm:space-x-6">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-white mb-2 sm:mb-4">Maîtrise Artisanale</h3>
                    <p className="text-sm sm:text-base text-white/70 font-light leading-relaxed">
                      Des décennies d'expérience dans l'art de l'accordage et de la restauration.
                      Chaque instrument est traité avec le respect et l'attention qu'il mérite,
                      préservant son âme musicale unique.
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card interactive-card rounded-xl sm:rounded-2xl p-6 sm:p-8 light-effect">
                <div className="flex items-start space-x-4 sm:space-x-6">
                  <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-white mb-2 sm:mb-4">Ancrage Alsacien</h3>
                    <p className="text-sm sm:text-base text-white/70 font-light leading-relaxed">
                      Implanté au cœur du Haut-Rhin, nous cultivons une relation de proximité
                      avec nos clients. Notre connaissance du terroir alsacien nous permet
                      d'offrir un service personnalisé et authentique.
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card interactive-card rounded-xl sm:rounded-2xl p-6 sm:p-8 light-effect">
                <div className="flex items-start space-x-4 sm:space-x-6">
                  <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-white mb-2 sm:mb-4">Passion Musicale</h3>
                    <p className="text-sm sm:text-base text-white/70 font-light leading-relaxed">
                      Au-delà de la technique, c'est une véritable passion pour la musique
                      qui anime notre travail. Nous comprenons l'importance de chaque note,
                      de chaque nuance dans l'expression artistique.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistiques */}
          <div className="mt-12 sm:mt-16 lg:mt-20 grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-2xl mx-auto px-4 sm:px-0">
            {[
              { number: "40+", label: "Années d'Expérience", icon: Clock },
              { number: "1000+", label: "Pianos Restaurés", icon: Music2 }
            ].map((stat, index) => (
              <div key={index} className="text-center glass-card rounded-xl sm:rounded-2xl p-6 interactive-card">
                <stat.icon className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400 mx-auto mb-3 sm:mb-4" />
                <div className="text-2xl sm:text-3xl font-light text-white mb-2">{stat.number}</div>
                <div className="text-white/60 font-light text-xs sm:text-sm uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;